import React from 'react';
import { Input } from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_INPUT_COMPACT_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';
import { getNumericInputMin, mergeNumericMin } from '../../valueConstraints';
import { CmsFieldError } from '../../CmsFieldError';
import { useCmsQuestionError } from '../../cmsFieldErrors';

export interface OASISNumberRenderProps {
  value: number | '';
  onChange: (v: number | null) => void;
  label: string;
  disabled: boolean;
  min?: number;
  max?: number;
}

export interface OASISNumberProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  min?: number;
  max?: number;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
  children?: (props: OASISNumberRenderProps) => React.ReactNode;
}

export function OASISNumber({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  min,
  max,
  controlClassName,
  inputClassName,
  children,
}: OASISNumberProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const cmsError = useCmsQuestionError(qid);
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);
  const constraints = question?.value_constraints;
  const effectiveMin = mergeNumericMin(getNumericInputMin(constraints), min);

  const raw = answers[qid];
  const value = raw == null ? '' : (Number(raw) as number);
  const onChange = (v: number | null) => onAnswerChange(qid, v);
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({
          value,
          onChange,
          label,
          disabled: inheritedDisabled,
          min: effectiveMin,
          max,
        })
      ) : (
        <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
          {!hideLabel && (
            <label
              className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
            >
              {label}
            </label>
          )}
          <Input
            type='number'
            disabled={inheritedDisabled}
            value={value}
            min={effectiveMin}
            max={max}
            onChange={e => {
              const n = parseFloat(e.target.value);
              onChange(isNaN(n) ? null : n);
            }}
            className={joinClasses(
              BESPOKE_INPUT_COMPACT_CLASS,
              resolvedControlClassName
            )}
          />
        </div>
      )}
      <CmsFieldError fieldId={`oasis-q-${qid}`} message={cmsError} />
    </div>
  );
}
